import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type DeviceOrientation = 'portrait' | 'landscape';

export interface DeviceInfo {
  type: DeviceType;
  orientation: DeviceOrientation;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
  isTouch: boolean;
  width: number;
  height: number;
}

// Breakpoints aplicados sobre o menor lado da viewport, para não variar com a orientação.
const MOBILE_MAX_SIDE = 600;
const TABLET_MAX_SIDE = 1024;

const RESIZE_DEBOUNCE_MS = 150;

const MOBILE_UA = /Mobi|iPhone|iPod|Android.*Mobile|Windows Phone|IEMobile|BlackBerry|BB10|Opera Mini/i;
const TABLET_UA = /iPad|Tablet|Android|Kindle|Silk|PlayBook/i;

/**
 * Serviço centralizado de detecção de dispositivo.
 * Combina User-Agent e largura da viewport, detecta orientação e
 * se atualiza automaticamente em resize / orientationchange.
 */
@Injectable({ providedIn: 'root' })
export class DeviceService implements OnDestroy {

  private readonly deviceSubject: BehaviorSubject<DeviceInfo>;
  private readonly eventsSubscription: Subscription;

  public readonly device$: Observable<DeviceInfo>;
  public readonly type$: Observable<DeviceType>;
  public readonly orientation$: Observable<DeviceOrientation>;
  public readonly isDesktop$: Observable<boolean>;

  constructor(private zone: NgZone) {
    this.deviceSubject = new BehaviorSubject<DeviceInfo>(this.detect());

    this.device$ = this.deviceSubject.asObservable();
    this.type$ = this.device$.pipe(map(device => device.type), distinctUntilChanged());
    this.orientation$ = this.device$.pipe(map(device => device.orientation), distinctUntilChanged());
    this.isDesktop$ = this.device$.pipe(map(device => device.isDesktop), distinctUntilChanged());

    // Escuta os eventos fora da zona do Angular e só reentra quando algo realmente muda.
    this.eventsSubscription = this.zone.runOutsideAngular(() =>
      merge(fromEvent(window, 'resize'), fromEvent(window, 'orientationchange'))
        .pipe(debounceTime(RESIZE_DEBOUNCE_MS))
        .subscribe(() => this.refresh())
    );
  }

  public get snapshot(): DeviceInfo {
    return this.deviceSubject.getValue();
  }

  public get isMobile(): boolean {
    return this.snapshot.isMobile;
  }

  public get isTablet(): boolean {
    return this.snapshot.isTablet;
  }

  public get isDesktop(): boolean {
    return this.snapshot.isDesktop;
  }

  public ngOnDestroy(): void {
    this.eventsSubscription.unsubscribe();
    this.deviceSubject.complete();
  }

  private refresh(): void {
    const current = this.snapshot;
    const next = this.detect();

    if (
      next.type !== current.type ||
      next.orientation !== current.orientation ||
      next.width !== current.width ||
      next.height !== current.height
    ) {
      this.zone.run(() => this.deviceSubject.next(next));
    }
  }

  private detect(): DeviceInfo {
    const width = window.innerWidth || document.documentElement.clientWidth || 0;
    const height = window.innerHeight || document.documentElement.clientHeight || 0;
    const isTouch = this.hasTouch();
    const type = this.detectType(width, height, isTouch);
    const orientation = this.detectOrientation(width, height);

    return {
      type,
      orientation,
      isMobile: type === 'mobile',
      isTablet: type === 'tablet',
      isDesktop: type === 'desktop',
      isPortrait: orientation === 'portrait',
      isLandscape: orientation === 'landscape',
      isTouch,
      width,
      height,
    };
  }

  /**
   * O User-Agent tem prioridade quando é conclusivo. Para UAs ambíguos
   * (ex.: iPadOS se identificando como Macintosh, modo "versão desktop" no celular)
   * a viewport decide, mas só em dispositivos touch — uma janela de desktop
   * redimensionada continua sendo desktop.
   */
  private detectType(width: number, height: number, isTouch: boolean): DeviceType {
    const ua = navigator.userAgent || '';
    const shortestSide = Math.min(width, height);

    if (MOBILE_UA.test(ua)) {
      return 'mobile';
    }

    if (TABLET_UA.test(ua)) {
      return shortestSide > 0 && shortestSide < MOBILE_MAX_SIDE ? 'mobile' : 'tablet';
    }

    const isIpadOs = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
    if (isIpadOs) {
      return 'tablet';
    }

    if (isTouch && this.hasCoarsePointer()) {
      if (shortestSide < MOBILE_MAX_SIDE) {
        return 'mobile';
      }
      if (shortestSide <= TABLET_MAX_SIDE) {
        return 'tablet';
      }
    }

    return 'desktop';
  }

  private detectOrientation(width: number, height: number): DeviceOrientation {
    if (window.matchMedia) {
      return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
    }

    return height >= width ? 'portrait' : 'landscape';
  }

  private hasTouch(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  private hasCoarsePointer(): boolean {
    return Boolean(window.matchMedia) && window.matchMedia('(pointer: coarse)').matches;
  }
}
